import { useCallback, useEffect, useRef, useState, type DragEvent, type FormEvent } from 'react';
import {
  ChevronRight,
  Download,
  File,
  FileArchive,
  FilePlus,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  Home,
  MoreVertical,
  PackageOpen,
  Pencil,
  RefreshCw,
  Save,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import type { MinecraftDirectory, MinecraftFile, MinecraftServer } from '@spawnwire/shared';
import { Button, clsx, ContextMenu, DropdownMenu, FullscreenDialog, IconButton, Input, Modal, Spinner, type MenuEntry } from '../../components/ui';
import { ApiError, errorMessage, http, uploadFile } from '../../lib/api';
import { ui } from '../../store/ui';

const ARCHIVE = /\.(zip|tar|tar\.gz|tgz|tar\.bz2|tbz2|tar\.xz|txz|gz|rar|7z)$/i;
/** Settings Spawnwire writes back into server.properties on every start. */
const MANAGED_PROPERTIES = 'Render distance, simulation distance and max players are set back to 6, 4 and 16 every time the server starts.';

const join = (dir: string, name: string) => (dir === '/' ? `/${name}` : `${dir}/${name}`);

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let n = bytes / 1024;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n >= 10 ? 0 : 1)} ${units[i]}`;
}

function fileIcon(f: MinecraftFile) {
  if (!f.isFile) return Folder;
  if (ARCHIVE.test(f.name)) return FileArchive;
  return f.editable ? FileText : File;
}

interface UploadItem {
  key: number;
  name: string;
  progress: number;
  error: string | null;
}

type NameDialog = { kind: 'folder' } | { kind: 'file' } | { kind: 'rename'; file: MinecraftFile };

interface EditorState {
  path: string;
  original: string;
  content: string;
}

let uploadKey = 0;

/**
 * Browse, edit, upload and download a hosted server's files. Staff get a read-only view of any server
 * through their own API path (`base`).
 */
export function FileManager({ server, uploadMaxBytes, base: baseOverride, readOnly = false }: { server: MinecraftServer; uploadMaxBytes: number; base?: string; readOnly?: boolean }) {
  const base = baseOverride ?? `/minecraft/servers/${server.id}/files`;
  const suspended = !!server.suspendReason;
  const ready = server.state !== 'installing' && server.state !== 'install_failed' && !suspended;
  const [dir, setDir] = useState('/');
  const [listing, setListing] = useState<MinecraftDirectory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [dragging, setDragging] = useState(false);
  const [nameDialog, setNameDialog] = useState<NameDialog | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const dirRef = useRef(dir);
  dirRef.current = dir;

  const load = useCallback(
    async (target: string) => {
      setLoading(true);
      try {
        const res = await http.get<MinecraftDirectory>(`${base}?dir=${encodeURIComponent(target)}`);
        // Ignore answers for a folder the person has already left.
        if (res.path !== dirRef.current) return;
        setListing(res);
        setError(null);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404 && target !== '/') {
          setDir('/');
          return;
        }
        setError(errorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [base],
  );

  useEffect(() => {
    setSelected(new Set());
    if (ready) void load(dir);
  }, [dir, ready, load]);

  const open = (path: string) => {
    if (path === dir) void load(path);
    else setDir(path);
  };

  async function openFile(f: MinecraftFile) {
    const path = join(dir, f.name);
    if (!f.isFile) return open(path);
    if (!f.editable) return download(f);
    setOpening(f.name);
    try {
      const { content } = await http.get<{ content: string }>(`${base}/contents?file=${encodeURIComponent(path)}`);
      setEditor({ path, original: content, content });
    } catch (err) {
      ui.toast(errorMessage(err), 'error');
    } finally {
      setOpening(null);
    }
  }

  async function download(f: MinecraftFile) {
    try {
      const { url } = await http.get<{ url: string }>(`${base}/download?file=${encodeURIComponent(join(dir, f.name))}`);
      // The node sends the file as an attachment, so following the link downloads it without leaving the page.
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      a.click();
    } catch (err) {
      ui.toast(errorMessage(err), 'error');
    }
  }

  async function unpack(f: MinecraftFile) {
    const id = ++uploadKey;
    setUploads((u) => [...u, { key: id, name: `Unpacking ${f.name}`, progress: -1, error: null }]);
    try {
      await http.post(`${base}/decompress`, { file: join(dir, f.name) });
      setUploads((u) => u.filter((x) => x.key !== id));
      ui.toast(`Unpacked ${f.name}.`, 'success');
      void load(dirRef.current);
    } catch (err) {
      setUploads((u) => u.map((x) => (x.key === id ? { ...x, error: errorMessage(err) } : x)));
    }
  }

  function remove(names: string[]) {
    const root = dir;
    ui.confirm({
      title: names.length === 1 ? `Delete ${names[0]}?` : `Delete ${names.length} items?`,
      body: 'Deleted files and folders are gone for good. Folders are deleted with everything in them.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        await http.post(`${base}/delete`, { root, files: names });
        setSelected(new Set());
        void load(root);
      },
    });
  }

  async function uploadAll(files: File[]) {
    if (files.length === 0) return;
    const root = dir;
    const items = files.map((file) => ({ file, item: { key: ++uploadKey, name: file.name, progress: 0, error: null as string | null } }));
    setUploads((u) => [...u, ...items.map((i) => i.item)]);
    const update = (key: number, patch: Partial<UploadItem>) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, ...patch } : x)));
    // One at a time: the node gets each file in full before the next one starts.
    for (const { file, item } of items) {
      if (file.size > uploadMaxBytes) {
        update(item.key, { error: `Too big. Uploads can be at most ${formatSize(uploadMaxBytes)} each.` });
        continue;
      }
      try {
        await uploadFile(`${base}/upload?dir=${encodeURIComponent(root)}`, file, (p) => update(item.key, { progress: p })).promise;
        setUploads((u) => u.filter((x) => x.key !== item.key));
      } catch (err) {
        update(item.key, { error: errorMessage(err) });
      }
    }
    if (dirRef.current === root) void load(root);
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (!ready) return;
    const entries = Array.from(e.dataTransfer.items).map((i) => i.webkitGetAsEntry?.());
    if (entries.some((entry) => entry?.isDirectory)) {
      ui.toast('Folders cannot be uploaded directly. Zip the folder, upload the zip, then choose Unpack.', 'error');
    }
    const files = Array.from(e.dataTransfer.files).filter((_, i) => !entries[i]?.isDirectory);
    void uploadAll(files);
  }

  function entriesFor(f: MinecraftFile): MenuEntry[] {
    if (readOnly) {
      return [
        { label: f.isFile ? (f.editable ? 'View' : 'Download') : 'Open', icon: f.isFile ? (f.editable ? FileText : Download) : FolderOpen, onSelect: () => void openFile(f) },
        { label: 'Download', icon: Download, onSelect: () => void download(f), hidden: !f.isFile || !f.editable },
      ];
    }
    return [
      { label: f.isFile ? (f.editable ? 'Edit' : 'Download') : 'Open', icon: f.isFile ? (f.editable ? Pencil : Download) : FolderOpen, onSelect: () => void openFile(f) },
      { label: 'Download', icon: Download, onSelect: () => void download(f), hidden: !f.isFile || !f.editable },
      { label: 'Unpack here', icon: PackageOpen, onSelect: () => void unpack(f), hidden: !f.isFile || !ARCHIVE.test(f.name) },
      { label: 'Rename', icon: Pencil, onSelect: () => setNameDialog({ kind: 'rename', file: f }) },
      { label: 'Delete', icon: Trash2, danger: true, separatorBefore: true, onSelect: () => remove([f.name]) },
    ];
  }

  const crumbs = dir === '/' ? [] : dir.slice(1).split('/');
  const files = listing?.path === dir ? listing.files : null;
  const allSelected = !!files && files.length > 0 && selected.size === files.length;

  return (
    <section
      aria-label="Files"
      className={clsx('relative rounded-xl border bg-sidebar transition-colors', dragging ? 'border-accent' : 'border-line')}
      onDragOver={(e) => {
        if (readOnly || !ready || !e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={onDrop}
    >
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
        <h3 className="mr-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted">
          <Folder className="size-4" aria-hidden /> Files
        </h3>
        <nav aria-label="Folder" className="flex min-w-0 flex-1 flex-wrap items-center gap-0.5 text-sm">
          <button type="button" onClick={() => open('/')} className="flex items-center gap-1 rounded px-1.5 py-0.5 text-muted hover:bg-hover hover:text-fg" aria-label="Server folder">
            <Home className="size-4" aria-hidden />
          </button>
          {crumbs.map((part, i) => (
            <span key={i} className="flex min-w-0 items-center gap-0.5">
              <ChevronRight className="size-3.5 shrink-0 text-faint" aria-hidden />
              <button
                type="button"
                onClick={() => open(`/${crumbs.slice(0, i + 1).join('/')}`)}
                className={clsx('truncate rounded px-1.5 py-0.5 hover:bg-hover', i === crumbs.length - 1 ? 'font-medium text-fg' : 'text-muted hover:text-fg')}
              >
                {part}
              </button>
            </span>
          ))}
        </nav>
        <div className="flex items-center gap-1">
          {selected.size > 0 && (
            <Button size="sm" variant="ghost" icon={Trash2} className="text-danger hover:text-danger" onClick={() => remove([...selected])}>
              Delete {selected.size}
            </Button>
          )}
          {!readOnly && <IconButton icon={FolderPlus} label="New folder" disabled={!ready} onClick={() => setNameDialog({ kind: 'folder' })} />}
          {!readOnly && <IconButton icon={FilePlus} label="New file" disabled={!ready} onClick={() => setNameDialog({ kind: 'file' })} />}
          <IconButton icon={RefreshCw} label="Refresh" disabled={!ready || loading} onClick={() => void load(dir)} />
          {!readOnly && (
            <Button size="sm" icon={Upload} disabled={!ready} onClick={() => picker.current?.click()}>
              Upload
            </Button>
          )}
          <input
            ref={picker}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              void uploadAll(Array.from(e.target.files ?? []));
              e.target.value = '';
            }}
          />
        </div>
      </header>

      {uploads.length > 0 && (
        <ul className="space-y-2 border-b border-line px-4 py-3" aria-label="Uploads">
          {uploads.map((u) => (
            <li key={u.key} className="text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className={clsx('truncate', u.error ? 'text-danger' : 'text-fg')}>
                  {u.name}
                  {u.error && <span className="text-muted"> — {u.error}</span>}
                </span>
                {u.error ? (
                  <IconButton icon={X} label="Dismiss" size="sm" onClick={() => setUploads((all) => all.filter((x) => x.key !== u.key))} />
                ) : u.progress < 0 ? (
                  <Spinner className="size-4" />
                ) : (
                  <span className="shrink-0 text-xs text-muted">{Math.round(u.progress * 100)}%</span>
                )}
              </div>
              {!u.error && u.progress >= 0 && (
                <div className="mt-1 h-1 overflow-hidden rounded-full bg-active">
                  <div className="h-full rounded-full bg-accent transition-[width]" style={{ width: `${u.progress * 100}%` }} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="min-h-[200px]">
        {!ready ? (
          <p className="px-4 py-8 text-center text-sm text-muted">
            {suspended ? 'Files are locked while this server is suspended.' : 'Files show up once the server has finished setting up.'}
          </p>
        ) : error && !files ? (
          <div className="flex flex-col items-center gap-3 px-4 py-8 text-center text-sm">
            <p className="text-danger">{error}</p>
            <Button size="sm" variant="secondary" onClick={() => void load(dir)}>
              Try again
            </Button>
          </div>
        ) : !files ? (
          <div className="flex justify-center py-10">
            <Spinner className="size-6" label="Loading files" />
          </div>
        ) : (
          <table className="w-full table-fixed text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-faint">
              <tr className="border-b border-line">
                <th className={clsx('w-10 py-2 pl-4', readOnly && 'invisible')}>
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={allSelected}
                    disabled={files.length === 0 || readOnly}
                    onChange={() => setSelected(allSelected ? new Set() : new Set(files.map((f) => f.name)))}
                    className="size-4 accent-[var(--p-accent)]"
                  />
                </th>
                <th className="py-2 font-semibold">Name</th>
                <th className="hidden w-24 py-2 text-right font-semibold sm:table-cell">Size</th>
                <th className="hidden w-44 py-2 pl-6 font-semibold md:table-cell">Modified</th>
                <th className="w-12 py-2 pr-2">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {files.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted">
                    {readOnly ? 'This folder is empty.' : 'This folder is empty. Drop files here or use Upload.'}
                  </td>
                </tr>
              )}
              {files.map((f) => {
                const Icon = fileIcon(f);
                const checked = selected.has(f.name);
                return (
                  <ContextMenu key={f.name} entries={entriesFor(f)}>
                    <tr className={clsx('group border-b border-line/60 last:border-0 hover:bg-hover', checked && 'bg-accent-soft')}>
                      <td className={clsx('py-1.5 pl-4', readOnly && 'invisible')}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${f.name}`}
                          checked={checked}
                          disabled={readOnly}
                          onChange={() =>
                            setSelected((s) => {
                              const next = new Set(s);
                              if (next.has(f.name)) next.delete(f.name);
                              else next.add(f.name);
                              return next;
                            })
                          }
                          className="size-4 accent-[var(--p-accent)]"
                        />
                      </td>
                      <td className="py-1.5">
                        <button type="button" onClick={() => void openFile(f)} className="flex w-full min-w-0 items-center gap-2.5 text-left">
                          {opening === f.name ? <Spinner className="size-4 shrink-0" /> : <Icon className={clsx('size-4 shrink-0', f.isFile ? 'text-muted' : 'text-accent')} aria-hidden />}
                          <span className="truncate text-fg group-hover:underline">{f.name}</span>
                        </button>
                      </td>
                      <td className="hidden py-1.5 text-right text-muted sm:table-cell">{f.isFile ? formatSize(f.size) : ''}</td>
                      <td className="hidden truncate py-1.5 pl-6 text-muted md:table-cell">
                        {new Date(f.modifiedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                      </td>
                      <td className="py-1 pr-2 text-right">
                        <DropdownMenu entries={entriesFor(f)} trigger={<IconButton icon={MoreVertical} label={`Actions for ${f.name}`} size="sm" />} />
                      </td>
                    </tr>
                  </ContextMenu>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {dragging && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl bg-main/80 text-sm font-semibold text-fg">
          <Upload className="mr-2 size-5" aria-hidden /> Drop to upload to {dir === '/' ? 'the server folder' : dir}
        </div>
      )}

      {nameDialog && (
        <NamePrompt
          dialog={nameDialog}
          onClose={() => setNameDialog(null)}
          onSubmit={async (name) => {
            if (nameDialog.kind === 'folder') await http.post(`${base}/folder`, { root: dir, name });
            else if (nameDialog.kind === 'file') {
              await http.put(`${base}/contents`, { file: join(dir, name), content: '' });
              setEditor({ path: join(dir, name), original: '', content: '' });
            } else await http.post(`${base}/rename`, { from: join(dir, nameDialog.file.name), to: join(dir, name) });
            void load(dir);
          }}
        />
      )}

      {editor && (
        <FileEditor
          state={editor}
          readOnly={readOnly}
          onChange={(content) => setEditor((e) => (e ? { ...e, content } : e))}
          onSave={async () => {
            await http.put(`${base}/contents`, { file: editor.path, content: editor.content });
            setEditor((e) => (e ? { ...e, original: e.content } : e));
            ui.toast('Saved.', 'success');
            void load(dirRef.current);
          }}
          onClose={() => setEditor(null)}
        />
      )}
    </section>
  );
}

function NamePrompt({ dialog, onClose, onSubmit }: { dialog: NameDialog; onClose: () => void; onSubmit: (name: string) => Promise<void> }) {
  const [name, setName] = useState(dialog.kind === 'rename' ? dialog.file.name : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const title = dialog.kind === 'folder' ? 'New folder' : dialog.kind === 'file' ? 'New file' : `Rename ${dialog.file.name}`;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const value = name.trim();
    if (!value) return;
    if (/[/\\"]/.test(value)) return setError('Names cannot contain / \\ or ".');
    setBusy(true);
    try {
      await onSubmit(value);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={title} size="sm">
      <form onSubmit={submit} className="space-y-3">
        <Input
          autoFocus
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
          maxLength={255}
          placeholder={dialog.kind === 'folder' ? 'plugins' : dialog.kind === 'file' ? 'notes.txt' : undefined}
          aria-label="Name"
          onFocus={(e) => {
            // Select the name without its extension, like a desktop file manager.
            const dot = e.target.value.lastIndexOf('.');
            e.target.setSelectionRange(0, dot > 0 ? dot : e.target.value.length);
          }}
        />
        {error && <p className="text-sm text-danger">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={busy} disabled={!name.trim()}>
            {dialog.kind === 'rename' ? 'Rename' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function FileEditor({
  state,
  readOnly,
  onChange,
  onSave,
  onClose,
}: {
  state: EditorState;
  readOnly: boolean;
  onChange: (content: string) => void;
  onSave: () => Promise<void>;
  onClose: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const dirty = state.content !== state.original;

  async function save() {
    if (saving || readOnly) return;
    setSaving(true);
    try {
      await onSave();
    } catch (err) {
      ui.toast(errorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  const close = () => (dirty ? setConfirmClose(true) : onClose());

  return (
    <FullscreenDialog open onClose={close} title={`Editing ${state.path}`}>
      <div
        className="flex min-w-0 flex-1 flex-col"
        onKeyDown={(e) => {
          if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
            e.preventDefault();
            void save();
          }
        }}
      >
        <header className="flex h-12 shrink-0 items-center gap-3 border-b border-line px-3">
          <IconButton icon={X} label="Close editor" onClick={close} />
          <span className="min-w-0 flex-1 truncate font-mono text-sm text-fg">
            {state.path}
            {dirty && <span className="ml-2 font-sans text-xs text-warning">Unsaved</span>}
          </span>
          {confirmClose ? (
            <span className="flex items-center gap-2 text-sm text-muted">
              <span className="hidden sm:inline">Discard your changes?</span>
              <Button size="sm" variant="ghost" onClick={() => setConfirmClose(false)}>
                Keep editing
              </Button>
              <Button size="sm" variant="danger" onClick={onClose}>
                Discard
              </Button>
            </span>
          ) : readOnly ? (
            <span className="text-xs text-muted">Read only</span>
          ) : (
            <Button size="sm" icon={Save} loading={saving} disabled={!dirty} onClick={() => void save()}>
              Save
            </Button>
          )}
        </header>
        {state.path === '/server.properties' && <p className="border-b border-line bg-sidebar px-4 py-2 text-xs text-muted">{MANAGED_PROPERTIES}</p>}
        <textarea
          value={state.content}
          readOnly={readOnly}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          autoFocus
          aria-label={`Contents of ${state.path}`}
          className="scroll-thin min-h-0 flex-1 resize-none bg-main p-4 font-mono text-sm leading-6 text-fg outline-none"
        />
      </div>
    </FullscreenDialog>
  );
}
